import { useTranslations as useNextTranslations } from "next-intl";
import { getTranslations as getNextTranslations } from "next-intl/server";
import type { TranslationKeys } from "./translation-types";

/**
 * Helper type to get all possible nested keys for a namespace
 */
type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}` | `${K}.${NestedKeys<T[K]>}`
          : `${K}`
        : never;
    }[keyof T]
  : never;

/**
 * Helper type to get all possible namespace paths (including nested ones)
 * e.g., "Kiosk" | "Kiosk.MedriteCheckin" | "AccountNavbar" | ...
 */
type NamespacePaths<T, Prefix extends string = ""> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ?
              | (Prefix extends "" ? K : `${Prefix}.${K}`)
              | NamespacePaths<T[K], Prefix extends "" ? K : `${Prefix}.${K}`>
          : never
        : never;
    }[keyof T]
  : never;

/**
 * Helper type to get the value at a nested path
 */
type GetValueAtPath<
  T,
  Path extends string,
> = Path extends `${infer Head}.${infer Tail}`
  ? Head extends keyof T
    ? GetValueAtPath<T[Head], Tail>
    : never
  : Path extends keyof T
    ? T[Path]
    : never;

/**
 * All valid namespace paths for translations
 */
type ValidNamespace = keyof TranslationKeys | NamespacePaths<TranslationKeys>;

/**
 * Get the keys available for a given namespace path
 */
type KeysForNamespace<NS extends string> = NS extends keyof TranslationKeys
  ? NestedKeys<TranslationKeys[NS]>
  : NestedKeys<GetValueAtPath<TranslationKeys, NS>>;

/**
 * Typed wrapper around next-intl's useTranslations hook
 * Provides compile-time type safety for translation keys
 *
 * Usage:
 * const t = useTranslations("AccountNavbar");
 * t("Home"); // ✅ TypeScript knows this key exists
 * t("InvalidKey"); // ❌ TypeScript error: Property 'InvalidKey' does not exist
 *
 * For nested namespaces:
 * const t = useTranslations("Kiosk.MedriteCheckin");
 * t("Welcome"); // ✅ TypeScript knows this key exists
 *
 * For nested keys:
 * const t = useTranslations("Eligibility");
 * t("UnderAge.Title"); // ✅ TypeScript knows this nested key exists
 * t("UnderAge.InvalidKey"); // ❌ TypeScript error
 */
export function useTranslations<T extends ValidNamespace>(namespace: T) {
  const t = useNextTranslations(namespace);

  return t as {
    <K extends KeysForNamespace<T>>(
      key: K,
      values?: Record<string, unknown>,
      formats?: Record<string, unknown>,
    ): string;
    rich: typeof t.rich;
    markup: typeof t.markup;
    raw: typeof t.raw;
    has: typeof t.has;
  };
}

/**
 * Typed wrapper around next-intl's getTranslations function (server-side)
 * Provides compile-time type safety for translation keys
 *
 * Usage:
 * const t = await getTranslations("AccountNavbar");
 * t("Home"); // ✅ TypeScript knows this key exists
 * t("InvalidKey"); // ❌ TypeScript error: Property 'InvalidKey' does not exist
 *
 * For nested namespaces:
 * const t = await getTranslations("Kiosk.MedriteCheckin");
 * t("Welcome"); // ✅ TypeScript knows this key exists
 *
 * For nested keys:
 * const t = await getTranslations("Eligibility");
 * t("UnderAge.Title"); // ✅ TypeScript knows this nested key exists
 * t("UnderAge.InvalidKey"); // ❌ TypeScript error
 */
export async function getTranslations<T extends ValidNamespace>(
  namespace: T,
  locale?: string,
) {
  const t = locale
    ? await getNextTranslations({ locale: locale, namespace: namespace })
    : await getNextTranslations(namespace);

  return t as {
    <K extends KeysForNamespace<T>>(
      key: K,
      values?: Record<string, unknown>,
      formats?: Record<string, unknown>,
    ): string;
    rich: typeof t.rich;
    markup: typeof t.markup;
    raw: typeof t.raw;
    has: typeof t.has;
  };
}

/**
 * Helper type to extract the keys from a translation namespace
 *
 * Usage:
 * type AccountNavbarKeys = TranslationNamespaceKeys<"AccountNavbar">;
 * // Result: "Home" | "Appointments" | "Profile" | "Payments" | "SignOut" | "ContactUs"
 *
 * For nested namespaces:
 * type EligibilityKeys = TranslationNamespaceKeys<"Eligibility">;
 * // Result: "UnderAge" | "UnderAge.Title" | "UnderAge.Tag" | "UnderAge.Footer" | "StateNotAvailable" | "StateNotAvailable.Title" | ...
 *
 * type KioskCheckinKeys = TranslationNamespaceKeys<"Kiosk.MedriteCheckin">;
 * // Result: "Welcome" | "PleaseTapBelowToBeginYourCheckIn" | ...
 */
export type TranslationNamespaceKeys<T extends ValidNamespace> =
  KeysForNamespace<T>;

/**
 * Helper type to get all possible translation keys
 *
 * Usage:
 * type AllKeys = AllTranslationKeys;
 * // Result: "AccountNavbar" | "AccountContactUs" | "AccountAppointments" | ...
 */
export type AllTranslationKeys = keyof TranslationKeys;

/**
 * Helper type to get all possible translation keys with their full paths
 *
 * Usage:
 * type FullKeys = FullTranslationKeys;
 * // Result: "AccountNavbar.Home" | "AccountNavbar.Appointments" | "AccountContactUs.PatientSupportInquiry" | ...
 */
export type FullTranslationKeys = {
  [K in keyof TranslationKeys]: {
    [P in keyof TranslationKeys[K]]: `${K & string}.${P & string}`;
  }[keyof TranslationKeys[K]];
}[keyof TranslationKeys];

/**
 * Typed translation function for passing down as props
 * This maintains type safety when passing translation functions to child components
 *
 * Usage:
 * type TCard = TypedTranslationFunction<"AppointmentCard">;
 * const t_card: TCard = useTranslations("AppointmentCard");
 *
 * // Or in component props:
 * interface Props {
 *   t_card: TypedTranslationFunction<"AppointmentCard">;
 * }
 *
 * // For nested namespaces:
 * type TKiosk = TypedTranslationFunction<"Kiosk.MedriteCheckin">;
 *
 * The `values` parameter is for interpolation variables like {name} in translations
 * The `formats` parameter is for date/number formatting options
 */
export type TypedTranslationFunction<T extends ValidNamespace> = ReturnType<
  typeof useTranslations<T>
>;
